import { AxiosResponse } from "axios";

import { axiosInstance, MessageResponse } from "./common";
import { KycType } from "./enrichedStakeholder";
import { FormType } from "./formConfigs";
import { VerificationAnomalyLevel } from "./physicalTechnicalVerifications";

export { VerificationAnomalyLevel };
export type { MessageResponse };

export enum ParentEntityType {
  CREDIT_ACCOUNT = "CREDIT_ACCOUNT",
  ACCOUNT = "ACCOUNT"
}

export enum DocumentEntityType {
  STAKEHOLDER_KYC_DETAILS = "STAKEHOLDER_KYC_DETAILS",
  STAKEHOLDER_BUSINESS_DETAILS = "STAKEHOLDER_BUSINESS_DETAILS",
}

export const CIN = "CIN" as const;

export type IdentityDocumentType = KycType | typeof CIN;

export const IDENTITY_DOCUMENT_TYPES: IdentityDocumentType[] = [
  ...Object.values(KycType),
  CIN,
];

export enum ProfileDocumentType {
  CREDIT_REPORT = "CREDIT_REPORT",
  BANK_STATEMENT = "BANK_STATEMENT",
  BANK_ACCOUNT_PROOF = "BANK_ACCOUNT_PROOF",
  EMPLOYMENT_PROOF = "EMPLOYMENT_PROOF",
  INCOME_PROOF = "INCOME_PROOF",
  RESIDENCE_ADDRESS_PROOF = "RESIDENCE_ADDRESS_PROOF",
  BUSINESS_ADDRESS_PROOF = "BUSINESS_ADDRESS_PROOF",
  RATION_CARD = "RATION_CARD",
  UAN = "UAN",
  EPFO = "EPFO",
  ITR = "ITR",
  FSSAI = "FSSAI",
  BUILDING_SANCTION_PLAN = "BUILDING_SANCTION_PLAN",
  TRADE_LICENSE = "TRADE_LICENSE",
  GST_CERTIFICATE = "GST_CERTIFICATE",
  ELECTRICITY_BILL = "ELECTRICITY_BILL",
  FORM_26AS = "FORM_26AS",
  RENT_AGREEMENT = "RENT_AGREEMENT",
  CA_CERTIFICATE = "CA_CERTIFICATE",
  SHOP_ACT_CERTIFICATE = "SHOP_ACT_CERTIFICATE",
  MCA = "MCA",
}

export const IDENTITY_DOCUMENT_TYPE_LABELS: Record<IdentityDocumentType, string> = {
  [KycType.PAN]: "PAN",
  [KycType.AADHAAR]: "Aadhaar",
  [KycType.UDYAM_CERTIFICATE]: "Udyam Certificate",
  [KycType.DRIVING_LICENSE]: "Driving License",
  [KycType.PASSPORT]: "Passport",
  [KycType.VOTER_ID]: "Voter ID",
  [CIN]: "CIN",
};

export const PROFILE_DOCUMENT_TYPE_LABELS: Record<ProfileDocumentType, string> =
  {
    [ProfileDocumentType.CREDIT_REPORT]: "Credit Report",
    [ProfileDocumentType.BANK_STATEMENT]: "Bank Statement",
    [ProfileDocumentType.BANK_ACCOUNT_PROOF]: "Bank Account Proof",
    [ProfileDocumentType.EMPLOYMENT_PROOF]: "Employment Proof",
    [ProfileDocumentType.INCOME_PROOF]: "Income Proof",
    [ProfileDocumentType.RESIDENCE_ADDRESS_PROOF]: "Residence Address Proof",
    [ProfileDocumentType.BUSINESS_ADDRESS_PROOF]: "Business Address Proof",
    [ProfileDocumentType.RATION_CARD]: "Ration Card",
    [ProfileDocumentType.UAN]: "UAN",
    [ProfileDocumentType.EPFO]: "EPFO",
    [ProfileDocumentType.ITR]: "ITR",
    [ProfileDocumentType.FSSAI]: "FSSAI",
    [ProfileDocumentType.BUILDING_SANCTION_PLAN]: "Building Sanction Plan",
    [ProfileDocumentType.TRADE_LICENSE]: "Trade License",
    [ProfileDocumentType.GST_CERTIFICATE]: "GST Certificate",
    [ProfileDocumentType.ELECTRICITY_BILL]: "Electricity Bill",
    [ProfileDocumentType.FORM_26AS]: "26AS",
    [ProfileDocumentType.RENT_AGREEMENT]: "Rent Agreement",
    [ProfileDocumentType.CA_CERTIFICATE]: "CA Certificate",
    [ProfileDocumentType.SHOP_ACT_CERTIFICATE]: "Shop Act Certificate",
    [ProfileDocumentType.MCA]: "MCA",
  };


export const identityFormType = (documentType: IdentityDocumentType) =>
  `${documentType}_IDENTITY_VERIFICATION` as FormType;

export const profileFormType = (documentType: ProfileDocumentType) =>
  `${documentType}_PROFILE_VERIFICATION` as FormType;

export interface IdentityVerification {
  verification_id: string;
  parent_entity_type: ParentEntityType;
  parent_entity_id: string;
  stakeholder_id: string;
  document_type: IdentityDocumentType;
  document_entity_type: DocumentEntityType;
  document_entity_id: string;
  onboarding_details?: Record<string, any> | null;
  current_details?: Record<string, any> | null;
  anomaly?: VerificationAnomalyLevel | null;
}

export interface ProfileVerification {
  verification_id: string;
  parent_entity_type: ParentEntityType;
  parent_entity_id: string;
  stakeholder_id: string;
  document_type: ProfileDocumentType;
  onboarding_details?: Record<string, any> | null;
  current_details?: Record<string, any> | null;
  anomaly?: VerificationAnomalyLevel | null;
}

export interface RowMeta {
  isAdded: boolean;
  isDirty: boolean;
}

export type IdentityVerificationRow = IdentityVerification & RowMeta;

export type ProfileVerificationRow = ProfileVerification & RowMeta;

export interface GetIdentityVerificationsResponse {
  verifications: IdentityVerification[];
}

export interface GetProfileVerificationsResponse {
  verifications: ProfileVerification[];
}

export interface UpsertIdentityVerificationsRequest {
  verifications: IdentityVerification[];
}

export interface UpsertProfileVerificationsRequest {
  verifications: ProfileVerification[];
}

export const IdentityVerificationsAPI = {
  get: async (caseId: string) => {
    const response: AxiosResponse<GetIdentityVerificationsResponse> =
      await axiosInstance.get(`/v1/cases/${caseId}/identity-verifications`);

    return response.data;
  },

  upsert: async (
    caseId: string,
    request: UpsertIdentityVerificationsRequest,
  ) => {
    const response: AxiosResponse<MessageResponse> = await axiosInstance.put(
      `/v1/cases/${caseId}/identity-verifications`,
      request,
    );

    return response.data;
  },
};

export const ProfileVerificationsAPI = {
  get: async (caseId: string) => {
    const response: AxiosResponse<GetProfileVerificationsResponse> =
      await axiosInstance.get(`/v1/cases/${caseId}/profile-verifications`);

    return response.data;
  },

  upsert: async (
    caseId: string,
    request: UpsertProfileVerificationsRequest,
  ) => {
    const response: AxiosResponse<MessageResponse> = await axiosInstance.put(
      `/v1/cases/${caseId}/profile-verifications`,
      request,
    );

    return response.data;
  },
};
